import React, { useState } from 'react';
import { X, Clock, User, Calendar, AlertCircle } from 'lucide-react';
import { format, parseISO, addMinutes } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { useToast } from '../common/ToastContainer';
import LoadingSpinner from '../common/LoadingSpinner';

interface CallRequest {
  id: string;
  patientName: string;
  patientEmail?: string;
  symptoms: string[];
  age: number;
  gender: string;
  requestedDate?: string;
  requestedSpecificTime?: string; // ✅ NEW: Specific time (HH:MM)
  requestedTimeRangeStart?: string; // DEPRECATED
  requestedTimeRangeEnd?: string; // DEPRECATED
  preferredCallTime?: string; // For backward compatibility
  timezone?: string;
  urgencyLevel?: string;
  needsMedicalCertificate?: boolean;
}

interface AcceptRequestModalProps {
  request: CallRequest;
  doctorId: string;
  doctorTimezone: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AcceptRequestModal: React.FC<AcceptRequestModalProps> = ({
  request,
  doctorId,
  doctorTimezone,
  onClose,
  onSuccess
}) => {
  const [selectedTime, setSelectedTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  // Generate time slot for the specific requested time
  // Generate time slot for the specific requested time
    const generateTimeSlots = () => {
      const slots: { value: string; label: string }[] = [];

      console.log('🔍 ACCEPT MODAL - Request data:', {
        id: request.id,
        requestedDate: request.requestedDate,
        requestedTime: (request as any).requestedTime,
        requestedSpecificTime: request.requestedSpecificTime,
        requestedTimeRangeStart: request.requestedTimeRangeStart,
        patientTimezone: request.timezone,
        doctorTimezone: doctorTimezone
      });

      // Extract date from requestedDate (format: "2026-03-08T00:00:00.000Z")
      let date = request.requestedDate;
      if (date && date.includes('T')) {
        const dateObj = parseISO(date);
        date = format(dateObj, 'yyyy-MM-dd');
        console.log('📅 Extracted date:', date);
      }

      // Get time - priority: requestedTime > requestedSpecificTime > requestedTimeRangeStart
      let timeStr = (request as any).requestedTime || request.requestedSpecificTime || request.requestedTimeRangeStart;

      // Convert HH:MM:SS format to HH:MM if needed
      if (timeStr && timeStr.includes(':')) {
        const parts = timeStr.split(':');
        timeStr = `${parts[0]}:${parts[1]}`;
      }

      console.log('🕐 Time string:', timeStr);

      if (!date || !timeStr) {
        console.error('❌ Missing date or time');
        return slots;
      }

      try {
        // Get patient's timezone (the timezone where the time is specified)
        const patientTimezone = request.timezone || 'Asia/Yangon';

        console.log('🌍 Patient timezone:', patientTimezone);
        console.log('🌍 Doctor timezone:', doctorTimezone);

        // Create datetime string in patient's timezone
        const patientDateTime = `${date}T${timeStr}:00`;
        console.log('🕐 Patient local time:', patientDateTime, 'in', patientTimezone);

        // Parse as patient's timezone and convert to UTC
        const dateObj = fromZonedTime(patientDateTime, patientTimezone);
        console.log('🕐 Converted to UTC:', dateObj.toISOString());

        // Convert to doctor's timezone for display
        const doctorDisplayTime = formatInTimeZone(
          dateObj,
          doctorTimezone,
          'h:mm a'
        );

        slots.push({
          value: dateObj.toISOString(),
          label: `${doctorDisplayTime} (requested time)`
        });

        console.log('✅ Created slot:', {
          patientTime: `${timeStr} (${patientTimezone})`,
          utcTime: dateObj.toISOString(),
          doctorTime: `${doctorDisplayTime} (${doctorTimezone})`
        });

      } catch (err) {
        console.error('❌ Error creating time slot:', err);
      }

      return slots;
    }

  const timeSlots = generateTimeSlots();

  const handleAccept = async () => {
    if (!selectedTime) {
      toast.warning('Please select a time', 'Choose a meeting time from the available slots');
      return;
    }

    setLoading(true);
    setError(null);
    setLoadingStep('Scheduling consultation...');

    try {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        toast.error('Session expired', 'Please log in again');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';

      // Use requestId if available, otherwise fall back to id
      const actualRequestId = (request as any).requestId || request.id;
      
      console.log('🔍 Accepting request with ID:', actualRequestId, 'Full request:', request);
      
      // Find the selected slot to show what we're sending
      const selectedSlot = timeSlots.find(slot => slot.value === selectedTime);
      console.log('⏰ DOCTOR SELECTED TIME:', {
        displayedToDoctor: selectedSlot?.label,
        doctorTimezone: doctorTimezone,
        utcValueBeingSent: selectedTime,
        utcParsed: new Date(selectedTime).toISOString(),
        yangonTime: formatInTimeZone(parseISO(selectedTime), 'Asia/Yangon', 'PPpp')
      });

      setLoadingStep('Confirming with patient...');
      
      const payload = {
        doctorId: doctorId,
        acceptedTime: selectedTime,
        doctorTimezone: doctorTimezone // Send doctor's timezone too
      };
      
      console.log('📤 Sending to backend:', payload);

      const response = await fetch(`${API_BASE_URL}/call-requests/${actualRequestId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401 || response.status === 403) {
        toast.error('Authentication failed', 'Your session has expired. Please log in again.');
        localStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        setLoadingStep('Creating Google Meet link...');
        
        const result = await response.json();
        const formattedTime = format(parseISO(selectedTime), 'PPp');
        
        setLoadingStep('Sending confirmation emails...');
        
        // Give a moment for the user to see the final step
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // Show success toast
        toast.success(
          'Meeting scheduled!', 
          `Consultation with ${request.patientName} on ${formattedTime}`
        );
        
        // Show Google Meet link if available
        if (result.googleMeetLink) {
          setTimeout(() => {
            toast.info('Google Meet link created', 'Check your email for the meeting link');
          }, 1000);
        }
        
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to accept request' }));
        
        // Check if request was already processed
        if (errorData.error && errorData.error.includes('already processed')) {
          toast.warning(
            'Request no longer available', 
            'Another doctor may have accepted this request. The list will refresh.'
          );
          // Close modal and trigger refresh
          onClose();
          onSuccess(); // This will trigger refresh in parent
        } else if (errorData.error && errorData.error.includes('not found')) {
          toast.warning(
            'Request not found', 
            'This request may have been cancelled or expired. The list will refresh.'
          );
          // Close modal and trigger refresh
          onClose();
          onSuccess(); // This will trigger refresh in parent
        } else {
          toast.error('Failed to accept request', errorData.error || 'Please try again');
          setError(errorData.error || 'Failed to accept request');
        }
      }
    } catch (err) {
      console.error('Failed to accept request:', err);
      toast.error('Network error', 'Please check your connection and try again');
      setError(err instanceof Error ? err.message : 'Failed to accept request. Please try again.');
    } finally {
      setLoading(false);
      setLoadingStep('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Accept Request - Choose Meeting Time</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Patient Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <User className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Patient Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-600">Name</p>
                  <p className="font-medium text-gray-900">{request.patientName}</p>
                </div>
                <div>
                  <p className="text-gray-600">Age & Gender</p>
                  <p className="font-medium text-gray-900">{request.age} • {request.gender}</p>
                </div>
                {request.patientEmail && (
                  <div className="col-span-2">
                    <p className="text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{request.patientEmail}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Symptoms */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">Symptoms</h3>
          <div className="flex flex-wrap gap-2">
            {request.symptoms.map((symptom, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full"
              >
                {symptom}
              </span>
            ))}
          </div>
        </div>

        {/* Requested Time */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <Calendar className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Requested Time</h3>
              {(() => {
                // Extract date from requestedDate (format: "2026-03-08T00:00:00.000Z")
                let date = request.requestedDate;
                if (date && date.includes('T')) {
                  date = date.split('T')[0];
                }
                
                // Get time - priority: requestedTime > requestedSpecificTime > requestedTimeRangeStart
                let timeStr = (request as any).requestedTime || request.requestedSpecificTime || request.requestedTimeRangeStart;
                
                // Convert HH:MM:SS to HH:MM
                if (timeStr && timeStr.includes(':')) {
                  const parts = timeStr.split(':');
                  timeStr = `${parts[0]}:${parts[1]}`;
                }
                
                if (!date || !timeStr) {
                  return <div className="text-gray-400">Time not specified</div>;
                }
                
                try {
                  // Get patient's timezone
                  const patientTimezone = request.timezone || 'Asia/Yangon';
                  
                  // Create datetime in patient's timezone
                  const patientDateTime = `${date}T${timeStr}:00`;
                  
                  // Parse as patient's timezone and convert to UTC
                  const utcDate = fromZonedTime(patientDateTime, patientTimezone);
                  
                  // Convert UTC to doctor's timezone for display
                  const formattedDate = formatInTimeZone(utcDate, doctorTimezone, 'MMMM d, yyyy');
                  const convertedTime = formatInTimeZone(utcDate, doctorTimezone, 'h:mm a');
                  
                  return (
                    <div className="space-y-1">
                      <p className="text-gray-700">
                        <span className="font-medium">{formattedDate}</span>
                        <br />
                        <span className="text-lg font-bold text-blue-600">{convertedTime}</span>
                        <span className="text-sm text-gray-500"> ({doctorTimezone.split('/').pop()?.replace(/_/g, ' ')})</span>
                      </p>
                    </div>
                  );
                } catch (err) {
                  console.error('Error converting time:', err);
                  return <div className="text-gray-400">Time not specified</div>;
                }
              })()}
            </div>
          </div>
        </div>

        {/* Time Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-900 mb-3">
            <Clock className="w-4 h-4 inline mr-2" />
            Confirm meeting time * <span className="text-gray-500 text-xs">(Your timezone: {doctorTimezone})</span>
          </label>
          
          {timeSlots.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
              <p className="text-yellow-800 text-sm font-medium mb-2">
                Loading requested time...
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot.value}
                  onClick={() => setSelectedTime(slot.value)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    selectedTime === slot.value
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {slot.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Additional Info */}
        {request.needsMedicalCertificate && (
          <div className="mb-6 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              📄 <strong>Medical Certificate Required</strong> - Patient has requested an MC
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Loading Progress Indicator */}
        {loading && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-blue-900 font-medium">{loadingStep}</p>
                <p className="text-blue-700 text-xs mt-1">This may take a few moments while we process everything...</p>
              </div>
            </div>
            
            {/* Progress steps */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-2 ${loadingStep.includes('Scheduling') ? 'bg-blue-600 animate-pulse' : 'bg-green-500'}`}></div>
                <span className={loadingStep.includes('Scheduling') ? 'text-blue-900 font-medium' : 'text-gray-600'}>
                  Scheduling consultation
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-2 ${loadingStep.includes('Confirming') ? 'bg-blue-600 animate-pulse' : loadingStep.includes('Creating') || loadingStep.includes('Sending') ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={loadingStep.includes('Confirming') ? 'text-blue-900 font-medium' : loadingStep.includes('Creating') || loadingStep.includes('Sending') ? 'text-gray-600' : 'text-gray-400'}>
                  Confirming with patient
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-2 ${loadingStep.includes('Creating') ? 'bg-blue-600 animate-pulse' : loadingStep.includes('Sending') ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className={loadingStep.includes('Creating') ? 'text-blue-900 font-medium' : loadingStep.includes('Sending') ? 'text-gray-600' : 'text-gray-400'}>
                  Creating Google Meet link
                </span>
              </div>
              <div className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-2 ${loadingStep.includes('Sending') ? 'bg-blue-600 animate-pulse' : 'bg-gray-300'}`}></div>
                <span className={loadingStep.includes('Sending') ? 'text-blue-900 font-medium' : 'text-gray-400'}>
                  Sending confirmation emails
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAccept}
            disabled={loading || !selectedTime || timeSlots.length === 0}
            className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="flex flex-col items-start">
                  <span className="text-sm">{loadingStep}</span>
                  <span className="text-xs opacity-75">Please wait...</span>
                </span>
              </span>
            ) : (
              'Accept & Schedule'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptRequestModal;
